import React, { useEffect, useContext, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { useObserver } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';
import { toast } from 'react-toastify';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import PredefinedExamUpdateStore from '../../../../stores/PredefinedExamViewStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';
import AuthStore from '../../../../stores/AuthStore';

import * as AccessType from '../../../../constants/accessTypes';
import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';
import * as ach from '../../../../utilities/accessHelper';

import './NewPredefinedExamTemplate.scss';

function NewPredefinedExamTemplate(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const predefinedExam = useContext(PredefinedExamUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const auth = useContext(AuthStore);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (predefinedExam.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleExamTemplateChange = event => {
        const { value } = event.target;
        predefinedExam.selectedExamTemplateId = value ? value : null;
        predefinedExam.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (predefinedExam.newTemplate.name &&
            (predefinedExam.userPredefinedTemplates.some(u => u.name.toLowerCase() === predefinedExam.newTemplate.name.toLowerCase()) ||
                predefinedExam.sharedPredefinedTemplates.some(s => s.name.toLowerCase() === predefinedExam.newTemplate.name.toLowerCase()))) {
            toast.error(() => <p>Prefill template name already exists.</p>, { position: 'top-center' });
        }
        else if (fn.validateForm(validateRef.current)) {
            predefinedExam.save()
                .then(data => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { data });
                        }
                    }
                })
        }
    }

    const handleNewTemplateName = (event) => {
        const { value } = event.target;
        predefinedExam.newTemplate.name = value ? value : null;
        predefinedExam.hasUnsavedChanges = true;
    }

    const handleNewTemplateAccessLevel = (event) => {
        const { value } = event.target;
        predefinedExam.newTemplate.isShared = value && value === 'true' ? true : false;
        predefinedExam.hasUnsavedChanges = true;
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                props.onCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={predefinedExam.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam')}
                        action='Manage'
                        category='Prefill Exam Templates'
                        className='exams'
                        onCancel={props.onCancel}
                    />
                    <div className='quick-drawer-body'>
                        {
                            predefinedExam.isReady ?
                                <FadeIn>
                                    <div className='predefined-exam-templates body-content'>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group validate validate-required mb-0'>
                                                        <label className='required' htmlFor='exam-template'><small>Exam Type</small></label>
                                                        <select
                                                            id='exam-template'
                                                            className='custom-select form-control'
                                                            value={predefinedExam.selectedExamTemplateId ? predefinedExam.selectedExamTemplateId : ''}
                                                            onChange={handleExamTemplateChange}
                                                        >
                                                            <option value=''></option>
                                                            {
                                                                predefinedExam.examTemplates && predefinedExam.examTemplates.length > 0 ?
                                                                    predefinedExam.examTemplates.map((t, ti) => {
                                                                        return <option
                                                                            key={`exam_template_${ti}`}
                                                                            value={t.id}
                                                                        >{t.name}</option>
                                                                    }) :
                                                                    <option value=''></option>
                                                            }
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group validate validate-required mb-0'>
                                                        <label className='required'><small>Name</small></label>
                                                        <input
                                                            type='text'
                                                            className='form-control'
                                                            spellCheck={false}
                                                            maxLength='100'
                                                            autoComplete='off'
                                                            value={predefinedExam.newTemplate.name ? predefinedExam.newTemplate.name : ''}
                                                            onChange={handleNewTemplateName}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label className='required'><small>Access level</small></label>
                                                        <div className='custom-control custom-radio mb-2'>
                                                            <input
                                                                id='predefined-exam-access-level-user'
                                                                type='radio'
                                                                name='predefinedExamAccessLevel'
                                                                className='custom-control-input'
                                                                value='false'
                                                                checked={!predefinedExam.newTemplate.isShared}
                                                                onChange={handleNewTemplateAccessLevel}
                                                            />
                                                            <label
                                                                htmlFor='predefined-exam-access-level-user'
                                                                className='custom-control-label'
                                                            >
                                                                Only available to you
                                                            </label>
                                                        </div>
                                                        {
                                                            ach.check(AccessType.UPDATE_EXAM_PREDEFINED_DATA_SHARED) ?
                                                                <div className='custom-control custom-radio mb-2'>
                                                                    <input
                                                                        id='predefined-exam-access-level-office'
                                                                        type='radio'
                                                                        name='predefinedExamAccessLevel'
                                                                        className='custom-control-input'
                                                                        value='true'
                                                                        checked={predefinedExam.newTemplate.isShared}
                                                                        onChange={handleNewTemplateAccessLevel}
                                                                    />
                                                                    <label
                                                                        htmlFor='predefined-exam-access-level-office'
                                                                        className='custom-control-label'
                                                                    >
                                                                        Shared with others at <span className='tt-underline'>{auth.currentTenant.displayName}</span>
                                                                    </label>
                                                                </div> : null
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </FadeIn> : renderQuickDrawerLoading()
                        }
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default NewPredefinedExamTemplate;